/**
 * DFC — Radar Financeiro (contas a pagar do MV).
 *
 * Pagina core do AURA (menu Analises). Consome os endpoints /financeiro/radar*
 * via o `api` (JWT automatico). Estrutura herdada do prototipo (5 abas, KPIs,
 * graficos), re-vestida na identidade do produto: paleta default, fundo claro,
 * cards brancos, accent teal. recharts pros graficos.
 * (accent = cor default do produto; wiring por operadora via branding.cor_primaria
 * e' follow-up — ver docs/produto-neutro-levantamento.md, frente 4.)
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Cell, CartesianGrid, LabelList, LineChart, Line,
} from "recharts";
import { BarChart3, AlertTriangle, Layers, TrendingUp, Filter, HelpCircle, X, RefreshCw } from "lucide-react";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";

const MESES = ["", "Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

// Paleta base do produto (accent teal = cor_primaria default var(--brand, #20a490))
const T = {
  bg: "#f5f7fa", card: "#ffffff", ink: "#2c3e50", inkSoft: "#7f8c8d",
  line: "#e8e8e8", teal: "var(--brand, #20a490)", tealSoft: "#e8f6f4",
  risco: "#e74c3c", riscoSoft: "#fdecea", ambar: "#f39c12", ambarSoft: "#fef5e7",
  azul: "#3498db", verde: "#27ae60",
};
const MONO = "'SF Mono','JetBrains Mono',Consolas,monospace";

const fmt = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtK = (v) => (v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : Number(v).toFixed(0));
const fmtN = (v) => Number(v || 0).toLocaleString("pt-BR"); // inteiros com separador de milhar
const fmtDec = (v, d = 2) => Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtP = (v, d = 1) => `${fmtDec(v, d)}%`; // percentual pt-BR (virgula)
const mesLabel = (m) => {
  const [a, me] = String(m).split("-");
  return `${MESES[Number(me)]}/${a.slice(2)}`;
};

/* ---------- primitivas ---------- */
const Chip = ({ nivel, children }) => {
  const c = { alto: [T.riscoSoft, T.risco], medio: [T.ambarSoft, T.ambar], info: [T.tealSoft, T.teal] }[nivel];
  return (
    <span style={{ background: c[0], color: c[1], fontSize: 11, fontWeight: 700, padding: "3px 9px",
      borderRadius: 99, letterSpacing: 0.4, textTransform: "uppercase", whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
};

const Card = ({ children, style }) => (
  <div style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 12, padding: 20, ...style }}>
    {children}
  </div>
);

// Tooltip por hover, sem lib. O ':hover' vem do bloco <style> injetado na pagina.
const Info = ({ texto }) => (
  <span className="dfc-info" style={{ position: "relative", display: "inline-flex", verticalAlign: "middle" }}>
    <HelpCircle size={13} color={T.inkSoft} style={{ cursor: "help", marginLeft: 4 }} />
    <span className="dfc-tip" style={{
      position: "absolute", bottom: "140%", left: "50%", transform: "translateX(-50%)",
      background: T.ink, color: "#fff", fontSize: 12, fontWeight: 400, lineHeight: 1.4,
      padding: "8px 10px", borderRadius: 8, width: 240, zIndex: 50, textTransform: "none",
      letterSpacing: 0, boxShadow: "0 4px 14px rgba(0,0,0,0.18)" }}>
      {texto}
    </span>
  </span>
);

const Kpi = ({ rotulo, valor, sub, cor, dica }) => (
  <Card style={{ flex: 1, minWidth: 150 }}>
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: T.inkSoft, display: "flex", alignItems: "center" }}>
      {rotulo}{dica && <Info texto={dica} />}
    </div>
    <div style={{ fontFamily: MONO, fontSize: 24, fontWeight: 700, color: cor || T.ink, margin: "6px 0 2px" }}>{valor}</div>
    {sub && <div style={{ fontSize: 12, color: T.inkSoft }}>{sub}</div>}
  </Card>
);

const Secao = ({ titulo, desc, dica, children }) => (
  <div style={{ marginBottom: 26 }}>
    <h3 style={{ fontSize: 16, color: T.ink, margin: "0 0 2px", fontWeight: 700, display: "flex", alignItems: "center" }}>
      {titulo}{dica && <Info texto={dica} />}
    </h3>
    {desc && <p style={{ fontSize: 13, color: T.inkSoft, margin: "0 0 14px", maxWidth: 660 }}>{desc}</p>}
    {children}
  </div>
);

const Linha = ({ children, onClick }) => (
  <div onClick={onClick}
    onMouseEnter={onClick ? (e) => (e.currentTarget.style.background = T.tealSoft) : undefined}
    onMouseLeave={onClick ? (e) => (e.currentTarget.style.background = "transparent") : undefined}
    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 6px",
      borderBottom: `1px solid ${T.line}`, gap: 10, flexWrap: "wrap", borderRadius: 6,
      cursor: onClick ? "pointer" : "default" }}>
    {children}
  </div>
);

const TH = { padding: "10px 8px", fontWeight: 600, position: "sticky", top: 0, background: "#fff" };
const TD = { padding: "7px 8px", color: T.ink };
const fmtData = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");

/* Modal de drill-down: os lancamentos por tras de um agregado. */
function DetalheModal({ filtro, mesSel, onFechar }) {
  const [d, setD] = useState(null);
  const [carregando, setCarregando] = useState(true);
  useEffect(() => {
    setCarregando(true);
    api.get("/financeiro/radar/detalhe", { params: { ...(mesSel ? { mes_caixa: mesSel } : {}), ...filtro } })
      .then((r) => setD(r.data)).catch(() => setD(null)).finally(() => setCarregando(false));
  }, [filtro, mesSel]);

  const titulo = filtro.ds_item_res || filtro.ds_fornecedor || (filtro.so_reembolso ? "Reembolsos" : "Lançamentos");
  return (
    <div onClick={onFechar} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 880, maxHeight: "85vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", borderBottom: `1px solid ${T.line}` }}>
          <div>
            <div style={{ fontWeight: 700, color: T.ink }}>{titulo}</div>
            {d && <div style={{ fontSize: 12, color: T.inkSoft }}>{fmtN(d.resumo.qt)} lançamentos · {fmt(d.resumo.vl_pago)}{mesSel ? ` · ${mesLabel(mesSel)}` : ""}</div>}
          </div>
          <button onClick={onFechar} style={{ background: "none", border: "none", cursor: "pointer", color: T.inkSoft }}><X size={18} /></button>
        </div>
        <div style={{ overflow: "auto", padding: "0 18px 14px" }}>
          {carregando && <div style={{ padding: 20, color: T.inkSoft }}>Carregando…</div>}
          {!carregando && d && d.linhas.length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
              <thead><tr style={{ textAlign: "left", color: T.inkSoft }}>
                <th style={TH}>Fornecedor</th><th style={TH}>Documento</th><th style={TH}>Item</th>
                <th style={TH}>Venc.</th><th style={TH}>Pagto</th><th style={{ ...TH, textAlign: "right" }}>Atraso</th>
                <th style={{ ...TH, textAlign: "right" }}>Pago</th><th style={{ ...TH, textAlign: "right" }}>Retenção</th>
              </tr></thead>
              <tbody>
                {d.linhas.map((l, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${T.line}` }}>
                    <td style={TD}>{l.ds_fornecedor}</td>
                    <td style={{ ...TD, fontFamily: MONO, color: T.inkSoft }}>{l.nr_documento}</td>
                    <td style={TD}>{l.ds_item_res}</td>
                    <td style={TD}>{fmtData(l.dt_vencimento)}</td>
                    <td style={TD}>{fmtData(l.dt_pagamento)}</td>
                    <td style={{ ...TD, textAlign: "right", color: l.atraso_dias > 30 ? T.risco : T.inkSoft }}>{l.atraso_dias != null ? `${l.atraso_dias}d` : "—"}</td>
                    <td style={{ ...TD, textAlign: "right", fontFamily: MONO, fontWeight: 700 }}>{fmt(l.vl_pago)}</td>
                    <td style={{ ...TD, textAlign: "right", fontFamily: MONO, color: T.azul }}>{fmt(l.vl_retencao)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!carregando && d && d.linhas.length === 0 && <div style={{ padding: 20, color: T.inkSoft }}>Nada neste recorte.</div>}
          {d && d.truncado && <div style={{ padding: "10px 2px", fontSize: 12, color: T.ambar }}>Mostrando os 500 maiores. Refine o filtro (mês, item ou fornecedor) pra ver o resto.</div>}
        </div>
      </div>
    </div>
  );
}

/* ---------- pagina ---------- */
export default function DFC() {
  const { user } = useAuth();
  const souAdmin = user?.perfil === "administrador";
  const [atualizando, setAtualizando] = useState(false);
  const [dados, setDados] = useState(null);
  const [meses, setMeses] = useState([]);
  const [mesSel, setMesSel] = useState("");
  const [aba, setAba] = useState("visao");
  const [erro, setErro] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [detalhe, setDetalhe] = useState(null); // filtro do drill-down aberto

  useEffect(() => {
    api.get("/financeiro/radar/meses").then((r) => setMeses(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setCarregando(true); setErro(null);
    api.get("/financeiro/radar", { params: mesSel ? { mes_caixa: mesSel } : {} })
      .then((r) => setDados(r.data))
      .catch((e) => setErro(e.response?.data?.detail || e.message))
      .finally(() => setCarregando(false));
  }, [mesSel]);

  async function atualizar() {
    const [a, m] = mesSel
      ? mesSel.split("-").map(Number)
      : [new Date().getFullYear(), new Date().getMonth() + 1];
    setAtualizando(true);
    try {
      const r = await api.post("/financeiro/ingerir", null, { params: { ano: a, mes: m } });
      const ins = r.data?.qt_inseridas ?? 0;
      const [rad, mm] = await Promise.all([
        api.get("/financeiro/radar", { params: mesSel ? { mes_caixa: mesSel } : {} }),
        api.get("/financeiro/radar/meses"),
      ]);
      setDados(rad.data); setMeses(mm.data || []);
      toast.success(`Atualizado (${a}-${String(m).padStart(2, "0")}): ${ins} novo(s) lançamento(s).`);
    } catch (e) {
      toast.error("Falha ao atualizar: " + (e.response?.data?.detail || e.message));
    } finally { setAtualizando(false); }
  }

  const nAnomalias = useMemo(() => {
    if (!dados) return 0;
    return (dados.anomalias?.duplicidades?.length || 0)
      + (dados.anomalias?.recorrentes?.length || 0)
      + (dados.sla_reembolso?.resumo?.qt_estouro_sla || 0);
  }, [dados]);

  const temHistorico = (dados?.serie?.mom?.length || 0) >= 2;

  const abas = [
    { id: "visao", nome: "Visão geral", Icon: BarChart3 },
    { id: "competencia", nome: "Competência × Caixa", Icon: Layers },
    { id: "anomalias", nome: "Anomalias", Icon: AlertTriangle },
    { id: "concentracao", nome: "Concentração", Icon: Layers },
    { id: "serie", nome: "Evolução mensal", Icon: TrendingUp },
  ];

  return (
    <div style={{ background: T.bg, minHeight: "100vh", color: T.ink, paddingBottom: 40 }}>
      <style>{`
        .dfc-tip { visibility: hidden; opacity: 0; transition: opacity .12s; pointer-events: none; }
        .dfc-info:hover .dfc-tip { visibility: visible; opacity: 1; }
        @keyframes dfc-spin { to { transform: rotate(360deg); } }
      `}</style>
      {detalhe && <DetalheModal filtro={detalhe} mesSel={mesSel} onFechar={() => setDetalhe(null)} />}
      {/* cabecalho claro, accent teal */}
      <div style={{ background: T.card, borderBottom: `1px solid ${T.line}`, borderTop: `3px solid ${T.teal}`, padding: "20px 28px 0" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.teal, letterSpacing: 1, textTransform: "uppercase" }}>
                DFC · Contas a Pagar {mesSel ? `· ${mesLabel(mesSel)}` : "· base completa"}
              </div>
              <h1 style={{ fontSize: 24, margin: "4px 0 4px", color: T.ink }}>Radar Financeiro</h1>
              <div style={{ color: T.inkSoft, fontSize: 13, marginBottom: 14 }}>
                {dados ? `${fmtN(dados.kpis.qt_lancamentos)} lançamentos · ${fmtN(dados.kpis.qt_fornecedores)} fornecedores` : "…"}
              </div>
            </div>
            <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, background: T.bg, border: `1px solid ${T.line}`,
                borderRadius: 8, padding: "6px 10px" }}>
                <Filter size={14} color={T.inkSoft} />
                <select value={mesSel} onChange={(e) => setMesSel(e.target.value)}
                  style={{ border: "none", background: "transparent", color: T.ink, fontSize: 13, outline: "none" }}>
                  <option value="">Todos os meses</option>
                  {meses.map((m) => <option key={m} value={m}>{mesLabel(m)}</option>)}
                </select>
              </div>
              {souAdmin && (
                <button onClick={atualizar} disabled={atualizando} title="Puxar o mês do Oracle e atualizar a base"
                  style={{ display: "flex", alignItems: "center", gap: 6, background: T.teal, color: "#fff",
                    border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 13, fontWeight: 600,
                    cursor: atualizando ? "default" : "pointer", opacity: atualizando ? 0.7 : 1 }}>
                  <RefreshCw size={14} style={atualizando ? { animation: "dfc-spin 0.9s linear infinite" } : undefined} />
                  {atualizando ? "Atualizando…" : "Atualizar"}
                </button>
              )}
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: MONO, color: T.teal, fontSize: 22, fontWeight: 700 }}>
                  {dados ? fmt(dados.kpis.vl_pago) : "—"}
                </div>
                <div style={{ color: T.inkSoft, fontSize: 12 }}>desembolso</div>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {abas.map((a) => (
              <button key={a.id} onClick={() => setAba(a.id)} style={{
                background: "none", border: "none", cursor: "pointer",
                borderBottom: aba === a.id ? `3px solid ${T.teal}` : "3px solid transparent",
                color: aba === a.id ? T.teal : T.inkSoft, padding: "10px 14px", fontSize: 13, fontWeight: 600,
                display: "flex", alignItems: "center", gap: 6 }}>
                <a.Icon size={15} /> {a.nome}
                {a.id === "anomalias" && nAnomalias > 0 && (
                  <span style={{ marginLeft: 2, background: T.risco, color: "#fff", borderRadius: 99, fontSize: 10, padding: "1px 6px", fontWeight: 700 }}>{nAnomalias}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: "22px auto 0", padding: "0 20px" }}>
        {erro && (
          <Card style={{ background: T.riscoSoft, borderColor: T.risco, color: T.risco }}>
            <b>Não foi possível carregar o radar.</b> {String(erro)}. Verifique se há dados carregados (ingestão).
          </Card>
        )}
        {!erro && (carregando || !dados) && (
          <div style={{ padding: 40, color: T.inkSoft }}>Carregando radar financeiro…</div>
        )}

        {!erro && !carregando && dados && (() => {
          const K = dados.kpis;
          return (
            <>
              {/* ===== VISAO GERAL ===== */}
              {aba === "visao" && (<>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 26 }}>
                  <Kpi rotulo="Valor bruto (rateio)" valor={fmt(K.vl_rateio)} sub="obrigação contábil"
                    dica="Soma do VL_RATEIO: o valor cheio da obrigação antes de qualquer retenção. É o custo bruto do período." />
                  <Kpi rotulo="Retenções na fonte" valor={fmt(K.vl_retencao)} sub={`${fmtP(K.pct_retencao)} do bruto`} cor={T.azul}
                    dica="VL_RATEIO menos VL_PAGO: o que foi retido na fonte (impostos) ou glosado. Não saiu do caixa." />
                  <Kpi rotulo="Assistencial" valor={fmtP(K.pct_assistencial)} sub={fmt(K.vl_assistencial)} cor={T.teal}
                    dica="Fatia do desembolso no setor PLANO GERAL (assistencial), separada do custo administrativo." />
                  <Kpi rotulo="Administrativo" valor={fmtP(100 - K.pct_assistencial)} sub={fmt(K.vl_administrativo)}
                    dica="Fatia do desembolso fora do PLANO GERAL: estrutura, apoio e demais setores." />
                </div>

                <Secao titulo="Onde o dinheiro foi" desc="Composição do desembolso por natureza (item de resultado). Clique numa barra para ver os lançamentos.">
                  <Card>
                    <ResponsiveContainer width="100%" height={Math.max(200, dados.composicao.length * 44)}>
                      <BarChart data={dados.composicao} layout="vertical" margin={{ left: 10, right: 64 }}>
                        <XAxis type="number" hide />
                        <YAxis type="category" dataKey="ds_item_res" width={210} tick={{ fontSize: 12, fill: T.inkSoft }} axisLine={false} tickLine={false} />
                        <Tooltip formatter={(v) => fmt(v)} />
                        <Bar dataKey="vl_pago" radius={[0, 6, 6, 0]} cursor="pointer" onClick={(dd) => dd && setDetalhe({ ds_item_res: dd.ds_item_res })}>
                          <LabelList dataKey="vl_pago" position="right" formatter={(v) => "R$ " + fmtK(v)} style={{ fontSize: 12, fontFamily: MONO, fill: T.ink }} />
                          {dados.composicao.map((c, i) => (
                            <Cell key={i} fill={["CREDENCIADOS", "REEMBOLSO"].includes(c.ds_item_res) ? T.teal : T.azul} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                </Secao>

                <Secao titulo="Retenção implícita por fornecedor"
                  desc="VL_RATEIO − VL_PAGO: impostos retidos ou glosa. O desvio compara o período com o padrão histórico do próprio fornecedor. Acima de 1,5 p.p. merece conferência fiscal. Clique num fornecedor para ver os lançamentos.">
                  <Card>
                    {dados.retencoes.map((r) => (
                      <Linha key={r.ds_fornecedor} onClick={() => setDetalhe({ ds_fornecedor: r.ds_fornecedor })}>
                        <div style={{ fontSize: 13, fontWeight: 600, flex: 1, minWidth: 200 }}>{r.ds_fornecedor}</div>
                        {Math.abs(r.desvio_pp || 0) >= 1.5 && <Chip nivel="medio">desvio {r.desvio_pp > 0 ? "+" : ""}{fmtDec(r.desvio_pp, 1)} p.p.</Chip>}
                        <div style={{ fontFamily: MONO, fontSize: 13, color: T.inkSoft }}>{fmt(r.vl_pago)} pago</div>
                        <div style={{ fontFamily: MONO, fontSize: 13, fontWeight: 700, color: T.azul }}>−{fmt(r.vl_retencao)} ({fmtP(r.pct_retencao)})</div>
                      </Linha>
                    ))}
                    {dados.retencoes.length === 0 && <div style={{ fontSize: 13, color: T.inkSoft }}>Nenhuma retenção no período.</div>}
                  </Card>
                </Secao>
              </>)}

              {/* ===== COMPETENCIA x CAIXA ===== */}
              {aba === "competencia" && (<>
                <Secao titulo="A cauda da competência"
                  dica="Competência é o mês em que a despesa nasceu (foi lançada); caixa é o mês em que ela foi paga. A 'cauda' são pagamentos cujo caixa aconteceu 2+ meses depois da competência: despesa antiga saindo agora. É um proxy de passivo represado (IBNR administrativo)."
                  desc="Despesa por competência de origem dentro do caixa filtrado. Barras vermelhas atravessaram 2+ fechamentos contábeis antes de virar caixa.">
                  <Card>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={dados.competencia.por_competencia.map((c) => ({ ...c, comp: `${MESES[c.mes_competencia]}/${String(c.ano_competencia).slice(2)}` }))} margin={{ top: 24, right: 10, left: 10 }}>
                        <CartesianGrid vertical={false} stroke={T.line} />
                        <XAxis dataKey="comp" tick={{ fontSize: 12, fill: T.inkSoft }} axisLine={false} tickLine={false} />
                        <YAxis hide />
                        <Tooltip formatter={(v) => fmt(v)} />
                        <Bar dataKey="vl_pago" radius={[6, 6, 0, 0]}>
                          <LabelList dataKey="vl_pago" position="top" formatter={(v) => "R$ " + fmtK(v)} style={{ fontSize: 11, fontFamily: MONO, fill: T.ink }} />
                          {dados.competencia.por_competencia.map((c, i) => (
                            <Cell key={i} fill={c.lag >= 2 ? T.risco : c.lag === 1 ? T.ambar : T.teal} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <div style={{ display: "flex", gap: 18, fontSize: 12, color: T.inkSoft, paddingTop: 6, flexWrap: "wrap" }}>
                      <span><span style={{ color: T.teal }}>●</span> competência corrente</span>
                      <span><span style={{ color: T.ambar }}>●</span> 1 mês de defasagem</span>
                      <span><span style={{ color: T.risco }}>●</span> 2+ meses (cauda)</span>
                    </div>
                  </Card>
                </Secao>

                <Secao titulo="Lançamentos da cauda (2+ meses)">
                  <Card>
                    {dados.competencia.lancamentos_cauda.map((r, i) => (
                      <Linha key={i}>
                        <Chip nivel={r.lag_competencia >= 3 ? "alto" : "medio"}>{r.lag_competencia} meses</Chip>
                        <div style={{ fontSize: 13, flex: 1, minWidth: 180 }}>
                          {r.ds_fornecedor}
                          <span style={{ color: T.inkSoft, fontFamily: MONO, fontSize: 11 }}> · {r.nr_documento}</span>
                        </div>
                        <div style={{ fontSize: 12, color: T.inkSoft }}>comp. {MESES[r.mes_competencia]}/{String(r.ano_competencia).slice(2)}</div>
                        <div style={{ fontFamily: MONO, fontSize: 13, fontWeight: 700 }}>{fmt(r.vl_pago)}</div>
                      </Linha>
                    ))}
                    {dados.competencia.lancamentos_cauda.length === 0 && <div style={{ fontSize: 13, color: T.inkSoft }}>Sem cauda no período: tudo pago dentro de 1 mês da competência.</div>}
                  </Card>
                </Secao>
              </>)}

              {/* ===== ANOMALIAS ===== */}
              {aba === "anomalias" && (<>
                <Secao titulo="Pares suspeitos de duplicidade"
                  desc="Mesmo beneficiário, mesmo valor, mesmo mês de pagamento, documentos distintos. Todo par merece um olhar antes do próximo lote.">
                  <Card>
                    {dados.anomalias.duplicidades.map((g, i) => (
                      <div key={i} onClick={() => setDetalhe({ ds_fornecedor: g.ds_fornecedor, vl_pago: g.vl_pago })}
                        onMouseEnter={(e) => (e.currentTarget.style.background = T.tealSoft)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        style={{ padding: "12px 6px", borderBottom: `1px solid ${T.line}`, borderRadius: 6, cursor: "pointer" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                          <div style={{ fontSize: 13, fontWeight: 700 }}>{g.ds_fornecedor}</div>
                          <Chip nivel={g.vl_pago >= 1000 ? "alto" : "medio"}>{g.qt_par}× {fmt(g.vl_pago)}</Chip>
                        </div>
                        <div style={{ fontFamily: MONO, fontSize: 11, color: T.inkSoft, marginTop: 4 }}>{(g.docs_par || []).join("  ·  ")}</div>
                      </div>
                    ))}
                    {dados.anomalias.duplicidades.length === 0 && <div style={{ fontSize: 13, color: T.inkSoft }}>Nenhum par suspeito no período.</div>}
                  </Card>
                </Secao>

                <Secao titulo={`Reembolsos fora do prazo (SLA ${dados.sla_reembolso.sla_dias} dias)`}
                  desc="Atraso sobre o vencimento. Acima do SLA legal é passivo regulatório (ANS/NIP), não só financeiro.">
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14 }}>
                    <Kpi rotulo="Pagos em atraso" valor={fmtN(dados.sla_reembolso.resumo.qt_atrasados)} sub={`média ${fmtDec(dados.sla_reembolso.resumo.media_atraso || 0, 0)} dias`}
                      dica="Reembolsos pagos depois do vencimento (atraso > 0), independente do tamanho do atraso." />
                    <Kpi rotulo="Estouro de SLA" valor={fmtN(dados.sla_reembolso.resumo.qt_estouro_sla)} sub={fmt(dados.sla_reembolso.resumo.vl_estouro_sla)} cor={T.risco}
                      dica={`Reembolsos pagos com mais de ${dados.sla_reembolso.sla_dias} dias de atraso. Acima disso é passivo regulatório (ANS/NIP), não só financeiro.`} />
                    <Kpi rotulo="Pior atraso" valor={`${dados.sla_reembolso.resumo.max_atraso}d`} cor={T.risco}
                      dica="Maior atraso de reembolso no recorte, em dias entre vencimento e pagamento." />
                  </div>
                  <Card>
                    {dados.sla_reembolso.piores.map((r, i) => (
                      <Linha key={i}>
                        <Chip nivel={r.atraso_dias > dados.sla_reembolso.sla_dias ? "alto" : "medio"}>{r.atraso_dias}d</Chip>
                        <div style={{ fontSize: 13, flex: 1, minWidth: 180 }}>{r.ds_fornecedor}</div>
                        <div style={{ fontFamily: MONO, fontSize: 11, color: T.inkSoft }}>{r.nr_documento}</div>
                        <div style={{ fontFamily: MONO, fontSize: 13, fontWeight: 700 }}>{fmt(r.vl_pago)}</div>
                      </Linha>
                    ))}
                  </Card>
                </Secao>

                <Secao titulo="Beneficiários recorrentes"
                  desc="3+ reembolsos no período. Recorrência com valores redondos é o padrão que alimenta o score de fraude (futuro).">
                  <Card>
                    {dados.anomalias.recorrentes.map((b) => (
                      <Linha key={b.ds_fornecedor} onClick={() => setDetalhe({ ds_fornecedor: b.ds_fornecedor, so_reembolso: true })}>
                        <div style={{ fontSize: 13, fontWeight: 600, flex: 1, minWidth: 180 }}>{b.ds_fornecedor}</div>
                        <div style={{ fontSize: 12, color: T.inkSoft }}>{fmtN(b.qt_reembolsos)} reembolsos{b.qt_redondos > 0 && ` · ${fmtN(b.qt_redondos)} redondos`}</div>
                        <div style={{ fontFamily: MONO, fontSize: 13, fontWeight: 700, color: b.vl_total > 5000 ? T.risco : T.ink }}>{fmt(b.vl_total)}</div>
                      </Linha>
                    ))}
                    <div style={{ fontSize: 12, color: T.inkSoft, paddingTop: 10 }}>
                      {fmtN(dados.anomalias.valores_redondos.qt)} reembolsos com valor redondo ({fmtP(dados.anomalias.valores_redondos.pct_do_lote || 0)} do período), somando {fmt(dados.anomalias.valores_redondos.vl_total)}.
                    </div>
                  </Card>
                </Secao>
              </>)}

              {/* ===== CONCENTRACAO ===== */}
              {aba === "concentracao" && (<>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 26 }}>
                  <Kpi rotulo="Maior fornecedor" valor={fmtP(dados.concentracao.share_top1_pct)} sub={dados.concentracao.pareto[0]?.ds_fornecedor} cor={T.risco} />
                  <Kpi rotulo="Índice HHI" valor={fmtN(dados.concentracao.hhi)}
                    sub={dados.concentracao.hhi > 2500 ? "concentração alta" : dados.concentracao.hhi > 1500 ? "concentração moderada" : "pulverizado"}
                    cor={dados.concentracao.hhi > 1500 ? T.ambar : T.teal}
                    dica="Herfindahl-Hirschman (0 a 10000): mede o quanto o gasto se concentra em poucos fornecedores. Até 1500 pulverizado, 1500–2500 moderado, acima de 2500 alto." />
                  <Kpi rotulo="Top 3" valor={fmtP(dados.concentracao.share_top3_pct)} sub="do desembolso" />
                </div>
                <Secao titulo="Pareto de fornecedores" desc="Quem mais recebeu, do maior pro menor. Clique numa barra para ver os lançamentos do fornecedor.">
                  <Card>
                    <ResponsiveContainer width="100%" height={Math.max(240, dados.concentracao.pareto.length * 32)}>
                      <BarChart data={dados.concentracao.pareto.map((p) => ({ ...p, nome: p.ds_fornecedor.length > 28 ? p.ds_fornecedor.slice(0, 26) + "…" : p.ds_fornecedor }))} layout="vertical" margin={{ left: 10, right: 64 }}>
                        <XAxis type="number" hide />
                        <YAxis type="category" dataKey="nome" width={215} tick={{ fontSize: 11, fill: T.inkSoft }} axisLine={false} tickLine={false} />
                        <Tooltip formatter={(v) => fmt(v)} />
                        <Bar dataKey="vl_pago" radius={[0, 6, 6, 0]} cursor="pointer" onClick={(dd) => dd && setDetalhe({ ds_fornecedor: dd.ds_fornecedor })}>
                          <LabelList dataKey="vl_pago" position="right" formatter={(v) => "R$ " + fmtK(v)} style={{ fontSize: 11, fontFamily: MONO, fill: T.ink }} />
                          {dados.concentracao.pareto.map((_, i) => (
                            <Cell key={i} fill={i === 0 ? T.risco : i < 2 ? T.ambar : T.azul} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                </Secao>
              </>)}

              {/* ===== EVOLUCAO MENSAL ===== */}
              {aba === "serie" && (<>
                {!temHistorico ? (
                  <Card style={{ background: T.tealSoft, border: `1px dashed ${T.teal}` }}>
                    <div style={{ fontSize: 13, color: T.teal, fontWeight: 600 }}>
                      A evolução mensal ativa com 2+ meses de caixa carregados. Com 3+ meses, entram os alertas de z-score por fornecedor.
                    </div>
                  </Card>
                ) : (<>
                  <Secao titulo="Desembolso mês a mês" desc="Total de caixa por mês com variação sobre o mês anterior.">
                    <Card>
                      <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={dados.serie.mom.map((m) => ({ ...m, mes: mesLabel(m.mes_caixa), vl: Number(m.vl) }))} margin={{ top: 10, right: 20, left: 10 }}>
                          <CartesianGrid vertical={false} stroke={T.line} />
                          <XAxis dataKey="mes" tick={{ fontSize: 12, fill: T.inkSoft }} axisLine={false} tickLine={false} />
                          <YAxis tickFormatter={fmtK} tick={{ fontSize: 11, fill: T.inkSoft, fontFamily: MONO }} axisLine={false} tickLine={false} />
                          <Tooltip formatter={(v, n) => (n === "vl" ? fmt(v) : v)} />
                          <Line type="monotone" dataKey="vl" stroke={T.teal} strokeWidth={2.5} dot={{ r: 4, fill: T.teal }} />
                        </LineChart>
                      </ResponsiveContainer>
                      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", paddingTop: 8 }}>
                        {dados.serie.mom.filter((m) => m.delta_pct != null).map((m) => (
                          <div key={m.mes_caixa} style={{ fontSize: 12 }}>
                            <span style={{ color: T.inkSoft }}>{mesLabel(m.mes_caixa)}: </span>
                            <span style={{ fontFamily: MONO, fontWeight: 700, color: m.delta_pct > 0 ? T.risco : T.teal }}>{m.delta_pct > 0 ? "+" : ""}{fmtDec(m.delta_pct, 1)}%</span>
                          </div>
                        ))}
                      </div>
                    </Card>
                  </Secao>

                  <Secao titulo="Alertas de série (z-score por fornecedor)"
                    desc={`Meses em que o gasto com um fornecedor saiu ${dados.serie.zscore_limiar}+ desvios-padrão da própria média. Requer 3+ meses de histórico.`}>
                    <Card>
                      {dados.serie.alertas_zscore.map((a, i) => (
                        <Linha key={i}>
                          <Chip nivel={Math.abs(a.zscore) >= 3 ? "alto" : "medio"}>z = {fmtDec(a.zscore, 2)}</Chip>
                          <div style={{ fontSize: 13, flex: 1, minWidth: 180 }}>{a.ds_fornecedor}</div>
                          <div style={{ fontSize: 12, color: T.inkSoft }}>{mesLabel(a.mes_caixa)}</div>
                          <div style={{ fontFamily: MONO, fontSize: 13, fontWeight: 700 }}>{fmt(a.vl_pago)}</div>
                        </Linha>
                      ))}
                      {dados.serie.alertas_zscore.length === 0 && <div style={{ fontSize: 13, color: T.inkSoft }}>Nenhum fornecedor fora do próprio padrão, ou ainda não há 3 meses de histórico.</div>}
                    </Card>
                  </Secao>
                </>)}
              </>)}
            </>
          );
        })()}
      </div>
    </div>
  );
}
